import React, { useState, useEffect, useMemo } from "react";
import { apiService } from "../../services/apiService";
import ReportYearCalendarInput from "./ReportYearCalendarInput";

/**
 * Year-wise site footprint baseline: one total count split by site classification (sums must match).
 * Stored in tenant report settings separately from annual budget.
 */
const Report20FootprintBaselineCard = ({
  footprintBaselineByYear,
  updateFootprintBaseline,
  canEdit,
  tenantId,
}) => {
  const [classifications, setClassifications] = useState([]);
  const [baselineYear, setBaselineYear] = useState("");
  const [baselineTarget, setBaselineTarget] = useState("");
  const [counts, setCounts] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const getEmptyCounts = () => {
    const next = {};
    classifications.forEach((c) => {
      next[c] = 0;
    });
    return next;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getSiteClassifications(tenantId);
        if (!cancelled && res?.success && Array.isArray(res.data)) {
          setClassifications(res.data);
        }
      } catch {
        if (!cancelled) setClassifications([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    if (!classifications.length) return;
    if (!baselineYear) {
      setBaselineTarget("");
      setCounts(getEmptyCounts());
      return;
    }
    const saved = footprintBaselineByYear?.[baselineYear];
    if (saved) {
      setBaselineTarget(String(saved.target ?? ""));
      const next = {};
      classifications.forEach((c) => {
        next[c] = saved.byClassification?.[c] ?? 0;
      });
      setCounts(next);
    } else {
      setBaselineTarget("");
      setCounts(getEmptyCounts());
    }
  }, [baselineYear, footprintBaselineByYear, classifications]);

  const savedYears = useMemo(
    () => Object.keys(footprintBaselineByYear || {}).sort((a, b) => Number(b) - Number(a)),
    [footprintBaselineByYear]
  );

  useEffect(() => {
    if (canEdit || baselineYear) return;
    if (!savedYears.length) return;
    setBaselineYear(savedYears[0]);
  }, [canEdit, savedYears, baselineYear]);

  const targetNum = useMemo(() => {
    const n = parseInt(baselineTarget, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }, [baselineTarget]);

  const classificationSum = useMemo(() => {
    return Object.values(counts).reduce((a, v) => a + (Number.isFinite(Number(v)) ? Math.round(Number(v)) : 0), 0);
  }, [counts]);

  const sumsMatch = targetNum != null && classificationSum === targetNum;

  const setCount = (key, raw) => {
    const digits = String(raw).replace(/\D/g, "");
    const n = digits === "" ? 0 : parseInt(digits, 10);
    setCounts((prev) => ({ ...prev, [key]: Number.isFinite(n) ? n : 0 }));
  };

  return (
    <div className="mb-5 rounded-2xl border border-gray-100 bg-white shadow-md overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white">
        <h3 className="text-sm font-bold text-slate-900">Footprint baseline target</h3>
        <p className="text-xs text-slate-500 mt-1">
          Enter a 4-digit year, then enter counts. Classification totals must equal the baseline target. Separate from annual
          budget.
        </p>
      </div>
      <div className="p-4">
        {message && (
          <div
            className={`mb-3 p-3 rounded-lg text-sm ${
              message.type === "success" ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"
            }`}
          >
            {message.text}
          </div>
        )}
        {canEdit && (
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <ReportYearCalendarInput
              id="footprint-baseline-year"
              label="Year"
              labelClassName="block text-xs font-medium text-slate-600 mb-1"
              yearValue={baselineYear}
              onYearChange={setBaselineYear}
              inputClassName="w-[11.5rem] bg-white border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            />
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Baseline target (sites)</label>
              <input
                type="text"
                inputMode="numeric"
                value={baselineTarget}
                onChange={(e) => setBaselineTarget(e.target.value.replace(/\D/g, ""))}
                placeholder="Total site count"
                className="w-36 bg-white border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              />
            </div>
            <button
              type="button"
              disabled={saving || !sumsMatch || !baselineYear}
              onClick={async () => {
                const y = baselineYear;
                if (!y || targetNum == null) {
                  setMessage({ type: "error", text: "Enter a valid year and baseline target." });
                  return;
                }
                setSaving(true);
                setMessage(null);
                const byClassification = { ...counts };
                const result = await updateFootprintBaseline({
                  [y]: { target: targetNum, byClassification },
                });
                setSaving(false);
                if (result.success) {
                  setMessage({ type: "success", text: "Footprint baseline saved." });
                  setBaselineYear("");
                  setBaselineTarget("");
                  setCounts(getEmptyCounts());
                } else {
                  setMessage({ type: "error", text: result.error || "Failed to save." });
                }
              }}
              className="px-4 py-2 bg-pink-600 text-white rounded-md text-sm font-medium hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        )}

        {!canEdit && savedYears.length > 0 && (
          <div className="mb-4">
            <ReportYearCalendarInput
              id="footprint-baseline-year-readonly"
              label="Year"
              labelClassName="block text-xs font-medium text-slate-600 mb-1"
              yearValue={baselineYear || savedYears[0]}
              onYearChange={setBaselineYear}
              inputClassName="w-[11.5rem] bg-white border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
          </div>
        )}

        {(!canEdit && savedYears.length === 0 ? null : (
          <>
        <p className="text-xs font-semibold text-slate-600 mb-2">By site classification</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-3">
          {classifications.map((c) => (
            <div key={c}>
              <label className="block text-[11px] font-medium text-slate-500 mb-0.5 truncate" title={c}>
                {c}
              </label>
              {canEdit ? (
                <input
                  type="text"
                  inputMode="numeric"
                  value={String(counts[c] ?? 0)}
                  onChange={(e) => setCount(c, e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-md py-1.5 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              ) : (
                <p className="text-sm font-medium text-slate-800 py-1.5">{counts[c] ?? 0}</p>
              )}
            </div>
          ))}
        </div>

        <div
          className={`text-xs font-medium rounded-lg px-3 py-2 ${
            sumsMatch ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-900"
          }`}
        >
          Classification total: <strong>{classificationSum}</strong>
          {targetNum != null ? (
            <>
              {" "}
              · Baseline target: <strong>{targetNum}</strong>
              {sumsMatch ? " · Match" : canEdit ? " · Must match to save" : ""}
            </>
          ) : (
            <span className="text-slate-600 font-normal"> · Enter a baseline target</span>
          )}
        </div>
          </>
        ))}

        {!canEdit && Object.keys(footprintBaselineByYear || {}).length === 0 && (
          <p className="text-sm text-slate-500 mt-3">No footprint baseline has been configured yet.</p>
        )}
        {Object.keys(footprintBaselineByYear || {}).length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-slate-600 mb-1">Saved by year</p>
            <ul className="text-sm text-slate-700 flex flex-wrap gap-x-6 gap-y-1">
              {Object.entries(footprintBaselineByYear).map(([y, row]) => (
                <li key={y}>
                  {y}: {row?.target ?? "—"} sites
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default Report20FootprintBaselineCard;
